
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, CheckCircle, Clock, XCircle } from "lucide-react";
import { format, isValid } from "date-fns";

// Local copy of the safeFormatDate helper — kept in this file so the summary
// page has no dependency on the Reports page.
const safeFormatDate = (value, fmt = "MMM d, yyyy") => {
  if (value === undefined || value === null || value === "") return "-";
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (!isValid(d)) return "-";
    return format(d, fmt);
  } catch {
    return "-";
  }
};

/**
 * ExcusalSummary — the "Excusal Requests Summary" panel that used to live on
 * the Reports & Summary page. Same data, same layout, just its own route so
 * it can hang off the "Excusal Requests" sidebar section as a sub-item.
 */
export default function ExcusalSummary() {
  const { data: excusals = [] } = useQuery({
    queryKey: ['excusals'],
    queryFn: () => base44.entities.ExcusalSubmission.list('-created_date'),
  });

  const pendingExcusals = excusals.filter((e) => e.status === 'pending').length;
  const approvedExcusals = excusals.filter((e) => e.status === 'approved').length;
  const deniedExcusals = excusals.filter((e) => e.status === 'denied').length;

  const downloadExcusals = () => {
    const rows = [
      ['Student', 'Subject', 'Teacher', 'Reason', 'Status', 'Submitted', 'Teacher Notes'],
      ...excusals.map((e) => [
        e.student_name || '',
        e.subject || '',
        e.teacher_name || '',
        (e.reason || '').replace(/"/g, '""'),
        e.status || '',
        safeFormatDate(e.submission_date),
        (e.teacher_notes || '').replace(/"/g, '""'),
      ]),
    ];
    const csv = rows.map((r) => r.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `excusal-requests-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="excusal-summary-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Excusal Requests Summary</h1>
            <p className="text-slate-500 mt-1">
              Overview of every student excusal submission — status counts and the ten most recent requests.
            </p>
          </div>
        </div>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Excusal Requests Summary
              </CardTitle>
              <Button
                onClick={downloadExcusals}
                className="bg-red-700 hover:bg-red-800"
                size="sm"
                data-testid="excusal-summary-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Excusals CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">Total Requests</p>
                <p className="text-3xl font-bold text-blue-900" data-testid="excusal-summary-total">
                  {excusals.length}
                </p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-700 font-medium">Pending</p>
                <p className="text-3xl font-bold text-orange-900" data-testid="excusal-summary-pending">
                  {pendingExcusals}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 font-medium">Approved</p>
                <p className="text-3xl font-bold text-green-900" data-testid="excusal-summary-approved">
                  {approvedExcusals}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-700 font-medium">Denied</p>
                <p className="text-3xl font-bold text-red-900" data-testid="excusal-summary-denied">
                  {deniedExcusals}
                </p>
              </div>
            </div>

            {excusals.length > 0 ? (
              <div className="space-y-3">
                {excusals.slice(0, 10).map((excusal) => (
                  <div
                    key={excusal.id}
                    className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-900">{excusal.student_name}</p>
                          <Badge
                            className={
                              excusal.status === 'pending'
                                ? 'bg-orange-100 text-orange-800'
                                : excusal.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }
                          >
                            {excusal.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {excusal.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {excusal.status === 'denied' && <XCircle className="w-3 h-3 mr-1" />}
                            {excusal.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600">
                          <strong>Subject:</strong> {excusal.subject} • <strong>Teacher:</strong> {excusal.teacher_name}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          <strong>Reason:</strong> {excusal.reason}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Submitted {safeFormatDate(excusal.submission_date)}
                        </p>
                        {excusal.proof_of_completion_url && (
                          <p className="text-xs text-blue-600 mt-1">
                            📎{' '}
                            <a
                              href={excusal.proof_of_completion_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            >
                              View proof
                            </a>
                          </p>
                        )}
                        {excusal.teacher_notes && (
                          <p className="text-xs text-slate-600 mt-2 italic bg-slate-50 p-2 rounded">
                            Teacher note: {excusal.teacher_notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No excusal requests yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
